/**
 * ConsentManagerToggleBox
 * Felix Kiesewetter <info@felixkiesewetter.de>
 * v2.0.0
 * 2021-03-18
 */
class ConsentManagerToggleBox
{

    /**
     * ConsentManagerToggleBox constructor
     *
     * @param checkbox
     * @returns {void}
     */
    constructor(checkbox)
    {
        const toggleBox = document.createElement('span');
        toggleBox.classList.add('consent-property-toggle');
        if (checkbox.checked) {
            toggleBox.classList.add('-enabled');
        }
        if (checkbox.getAttribute('readonly')) {
            toggleBox.classList.add('-readonly');
        }
        else {
            checkbox.parentNode.addEventListener('click', (e) => {
                e.preventDefault();
                toggleBox.classList.toggle('-enabled');
                checkbox.checked = toggleBox.classList.contains('-enabled');
            });
        }
        checkbox.style.display = 'none';
        window.addEventListener('consentSettingsUpdated', () => {
            toggleBox.classList.toggle('-enabled', checkbox.checked);
        });
        checkbox.parentNode.insertBefore(toggleBox, checkbox);
    }

}

/**
 * ConsentManagerTranslationService
 * Felix Kiesewetter <info@felixkiesewetter.de>
 * v2.1.1
 * 2021-06-25
 */
class ConsentManagerTranslationService
{

    /**
     * ConsentManagerTranslationService constructor
     *
     * @param {string} privacyUri
     * @param {string} language
     * @returns {void}
     */
    constructor(privacyUri , language=null)
    {
        this.privacyUri = privacyUri;
        this.language = language || (document.documentElement.getAttribute('lang') || 'en');
        if (this.language.length !== 2) {
            this.language = this.language.substring(0, 2).toLowerCase();
        }
        this.translations = {
            de: {
                acceptAllLabel: 'Alle akzeptieren',
                acceptSelectedLabel: 'Auswahl akzeptieren',
                interfaceHeader: 'Wir nehmen Datenschutz ernst',
                interfaceText: `Wir tauschen Daten mit Drittanbietern aus, die uns helfen, unser Webangebot zu verbessern. Um bestimmte Dienste verwenden zu dürfen, benötigen wir Ihre Einwilligung. Indem Sie „%A“ klicken, stimmen Sie (jederzeit widerruflich) dieser Datenverarbeitung zu. Unter „%O“ können Sie Ihre Einstellungen ändern oder die Datenverarbeitung ablehnen. Weitere Informationen finden Sie in unserer %P`,
                privacyLinkLabel: 'Datenschutzerklärung',
                propertyLabelAnalytics: 'Google Analytics Tracking',
                propertyLabelGooglegooglemaps: 'Google Maps Kartendienste',
                propertyLabelGooglefonts: 'Google Fonts Schriften',
                propertyLabelYoutube: 'YouTube Videos',
                propertyLabelSendinblue: 'Sendinblue Tracking',
                propertyLabelLinkedin: 'LinkedIn Tracking',
                propertyLabelFacebook: 'Facebook Pixel Tracking',
                propertyLabelTagmanager: 'Google Tag Manager',
                queuePageReloadNotice: 'Drücken Sie „OK“, um die Seite neu zu laden und Ihre Einstellungen zu übernehmen.',
                requireConsent: 'Inhalt(e) geblockt. Klicken Sie hier, um Ihre Datenschutzeinstellungen anzupassen',
                requiredCookiesLabel: 'Notwendige Cookies',
                showOptionsLabel: 'Einstellungen'
            },
            en: {
                acceptAllLabel: 'Accept all',
                acceptSelectedLabel: 'Accept selected',
                interfaceHeader: 'We take data protection seriously',
                interfaceText: `We share data with third party service providers to help us improve our website. In order to use certain services, we need your consent. By clicking "%A", you agree (revocable at any time) to this data processing. Under "%O" you can change your settings or refuse data processing. You can find further information in our %P`,
                privacyLinkLabel: 'Privacy policy',
                propertyLabelAnalytics: 'Google Analytics Tracking',
                propertyLabelGooglegooglemaps: 'Google Maps Services',
                propertyLabelGooglefonts: 'Google Fonts',
                propertyLabelYoutube: 'YouTube Videos',
                propertyLabelSendinblue: 'Sendinblue Tracking',
                propertyLabelLinkedin: 'LinkedIn Tracking',
                propertyLabelFacebook: 'Facebook Pixel Tracking',
                propertyLabelTagmanager: 'Google Tag Manager',
                queuePageReloadNotice: 'Hit „OK“ to reload the page and apply your settings.',
                requireConsent: 'Content blocked. Click here to adjust your privacy settings',
                requiredCookiesLabel: 'Required cookies',
                showOptionsLabel: 'Settings'
            },
            fr: {
                acceptAllLabel: 'Tous acceptent',
                acceptSelectedLabel: 'Accepter la sélection',
                interfaceHeader: 'Nous prenons la protection des données au sérieux',
                interfaceText: `Nous partageons des données avec des prestataires de services tiers pour nous aider à améliorer notre site web. Afin d'utiliser certains services, nous avons besoin de votre consentement. En cliquant sur "%A", vous donnez votre accord (révocable à tout moment) à ce traitement des données. Sous "%O", vous pouvez modifier vos paramètres ou refuser le traitement des données. Vous trouverez de plus amples informations dans notre %P`,
                privacyLinkLabel: 'Privacy policy',
                propertyLabelAnalytics: 'Suivi de Google Analytics',
                propertyLabelGooglegooglemaps: 'Services Google Maps',
                propertyLabelGooglefonts: 'Google Fonts',
                propertyLabelYoutube: 'Vidéos sur YouTube',
                propertyLabelSendinblue: 'Suivi de Sendinblue',
                propertyLabelLinkedin: 'Suivi de LinkedIn',
                propertyLabelFacebook: 'Suivi de Facebook',
                propertyLabelTagmanager: 'Google Tag Manager',
                queuePageReloadNotice: 'Appuyez sur "OK" pour recharger la page et appliquer vos paramètres.',
                requireConsent: 'Contenu(s) bloqué(s). Cliquez ici pour ajuster vos paramètres de confidentialité',
                requiredCookiesLabel: 'Cookies nécessaires',
                showOptionsLabel: 'Paramètres'
            }
        };
    }

    /**
     * Get translated string by key
     *
     * @param key
     * @returns {string}
     */
    get(key='')
    {
        let translation = '';
        if ('undefined' === this.translations[this.language]) return '';
        else if ('undefined' === this.translations[this.language][key]) return '';
        else {
            translation = this.translations[this.language][key];
            if (new RegExp('%A').test(translation)) {
                translation = translation.replace('%A', this.translations[this.language].acceptAllLabel);
            }
            if (new RegExp('%P').test(translation)) {
                translation = translation.replace('%P', `<a href="${this.privacyUri}" target="_blank">${this.translations[this.language].privacyLinkLabel}`);
            }
            if (new RegExp('%O').test(translation)) {
                translation = translation.replace('%O', this.translations[this.language].showOptionsLabel);
            }
        }
        return translation;
    }

}

/**
 * ConsentManager
 * Felix Kiesewetter <info@felixkiesewetter.de>
 * v2.1.1
 * 2021-06-25
 */
class ConsentManager
{

    /**
     * Class constructor
     *
     * @param options
     * @returns {void}
     */
    constructor(options)
    {
        if (!options.privacyUri) {
            options.debug && console.error('[ConsentManager]', 'constructor()', 'The option privacyUri is mandatory');
            return;
        }
        this.translationService = new ConsentManagerTranslationService(options.privacyUri);
        this.setSettings(options);
        this.setLocalStorageIdentifier();
        this.errors = [];
        this.requireUserInteraction = false;
        this.queuePageReloadTimeout = null;
        this.updateEvent = new CustomEvent('consentSettingsUpdated');
        this.consentSettings = this.getConsentSettings();
        for (const key in this.consentSettings) {
            const functionName = `getConsentFor${key.charAt(0).toUpperCase() + key.slice(1)}`;
            this[functionName] = () => {
                return this.consentSettings[key];
            };
        };
        this.getInterface();
        this.fingerPrintIcon = this.getFingerPrintIcon();
        this.init();
    }

    /**
     * Initialise the consent manager
     *
     * @returns {void}
     */
    init()
    {
        this.disableFrames();
        this.bindFrames();
        this.clearCookies();
        this.addEventHandlers();
        if (this.requireUserInteraction) {
            this.showInterface();
        }
    }

    /**
     * Clear all youtube and googlemaps cookies if consent is not given
     *
     * @returns {void}
     */
    clearCookies()
    {
        const setCookie = (name, value, days) => {
            var d = new Date;
            d.setTime(d.getTime() + 24*60*60*1000*days);
            document.cookie = name + "=" + value + ";path=/;expires=" + d.toGMTString();
        };
        if (false === this.getConsentStatusForProperty('youtube')) {
            setCookie('DV', '', -1);
            setCookie('NID', '', -1);
            setCookie('1P_JAR', '', -1);
            setCookie('OGPC', '', -1);
            setCookie('CONSENT', '', -1);
        }
    }

    /**
     * Catch frames
     *
     * @returns {void}
     */
    disableFrames()
    {
        if (true !== this.settings.disableFrames) {
            return;
        }
        const frames = document.querySelectorAll('iframe[src*="/googlemaps/"], iframe[src*="youtu"]');
        if (frames) {
            for (const frame of frames) {
                const src = frame.src;
                Object.keys(this.settings.patterns).forEach((property) => {
                    if (new RegExp(this.settings.patterns[property]).test(src)) {
                        if (false === this.getConsentStatusForProperty(property)) {
                            frame.setAttribute('data-src', frame.src);
                            frame.removeAttribute('src');
                            frame.setAttribute('data-consent', property);
                        }
                    }
                });
            }
        }
    }

    /**
     * Bind frames to the consent manager
     *
     * @param {node} frame
     * @returns {void}
     */
    bindFrames()
    {
        const frames = document.querySelectorAll('iframe[data-src]');
        const getProperty = (src) => {
            let property = null
            Object.keys(this.settings.patterns).forEach((p) => {
                if (new RegExp(this.settings.patterns[p]).test(src)) {
                    property = p;
                }
            });
            return property;
        };
        for (const frame of frames) {
            const property = frame.getAttribute('data-consent') || getProperty(frame.getAttribute('data-src'));
            if (null === property) {
                return;
            }
            else if (this.getConsentStatusForProperty(property)) {
                this.enableFrame(frame);
            }
            else {
                const parent = frame.parentNode;
                const wrapper = document.createElement('aside');
                const text = document.createElement('p');
                text.classList.add('consent-required-text');
                text.innerHTML = this.translationService.get('requireConsent');
                text.addEventListener('click', () => {
                    this.showInterface(true);
                });
                window.addEventListener('consentSettingsUpdated', () => {
                    if (this.getConsentStatusForProperty(property)) {
                        this.enableFrame(frame, parent, wrapper);
                    }
                });
                parent.insertBefore(wrapper, frame);
                wrapper.classList.add('consent-required');
                wrapper.appendChild(frame);
                wrapper.appendChild(text);
            }
        }
    }

    /**
     * Enable a frame
     *
     * @param {node} frame
     * @param {node} parent
     * @param {node} wrapper
     */
    enableFrame(frame, parent=null, wrapper=null)
    {
        frame.src = frame.getAttribute('data-src');
        frame.removeAttribute('data-src');
        if (parent && wrapper) {
            parent.insertBefore(frame, wrapper);
            parent.removeChild(wrapper);
        }
    }

    /**
     * Add event handlers
     *
     * @returns {void}
     */
    addEventHandlers()
    {
        if (this.showAllLink && this.fieldset) {
            this.showAllLink.addEventListener('click', () => {
                this.fieldset.style.display = 'block';
                this.settings.interfaceMode = 'accept-selected';
                this.acceptSelectedButton.style.display = 'inline-block';
                this.showAllLink.remove();
            });
        }
        if (this.acceptAllButton) {
            this.acceptAllButton.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                this.setConsentSettings(true);
            });
        }
        if (this.acceptSelectedButton) {
            this.acceptSelectedButton.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                this.setConsentSettings(false);
            });
        }
        for (const trigger of document.querySelectorAll('[rel="consent-manager"]')) {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.showInterface(true);
            });
        }
    }

    /**
     * Get consent settings
     *
     * Retrieves current consent settings from localStorage or sets a default
     * If the consent settings have changed (new or changed properties) will also reset all
     *
     * @returns {object}
     */
    getConsentSettings()
    {
        let consentSettings = {};
        try {
            consentSettings = JSON.parse(localStorage.getItem(this.localStorageIdentifier)) || {};
            if (Object.keys(consentSettings).length === 0) {
                this.requireUserInteraction = true;
            }
            if (!this.hasSameSettingsAsBefore(consentSettings)) {
                consentSettings = this.getDefaultConsentSettings(); // TODO: dont reset -> merge old with new settings getMergedConsentSettings()
                this.requireUserInteraction = true;
            }
            else {
                Object.keys(consentSettings).forEach((property) => {
                    if (consentSettings[property] === true) {
                        if (typeof this.settings.properties[property].id !== 'undefined') {
                            this.optIn(property);
                        }
                        else if (typeof this.settings.properties[property].enableScriptTag !== 'undefined') {
                            this.enableScriptTag(this.settings.properties[property].enableScriptTag);
                        }
                    }
                    else if (typeof this.settings.properties[property].id !== 'undefined') {
                        this.optOut(property, false);
                    }
                });
            }
            window.dispatchEvent(this.updateEvent);
        }
        catch (error) {
            this.settings.debug && console.error('[ConsentManager]', 'getConsentSettings()', error);
        }
        this.settings.debug && console.log('[ConsentManager]', 'getConsentSettings()', consentSettings);
        return consentSettings;
    }

    /**
     * Set local storage identifier
     */
    setLocalStorageIdentifier()
    {
        if (null !== this.settings.localStorageIdentifier) {
            this.localStorageIdentifier = this.settings.localStorageIdentifier;
        }
        else {
            const domain = window.location.host.split('.')[1];
            const subdomain = window.location.host.split('.')[0];
            this.localStorageIdentifier = `${domain.charAt(0).toUpperCase() + domain.slice(1)}.${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)}.ConsentManager.ConsentSettings`;
        }
        this.settings.debug && console.log('[ConsentManager]', 'setLocalStorageIdentifier()', this.localStorageIdentifier);
    }

    /**
     * Set consent settings
     *
     * @param {boolean} acceptAll
     * @returns {void}
     */
    setConsentSettings(acceptAll=false)
    {
        const checkboxes = this.fieldset.querySelectorAll('input[type="checkbox"][name]:not([readonly])');
        if (true === acceptAll) {
            for (const checkbox of checkboxes) {
                checkbox.checked = true;
            }
        }
        for (const checkbox of checkboxes) {
            const property = checkbox.name.split('[')[1].split(']')[0];
            if (checkbox.checked && !this.consentSettings[property]) {
                this.consentSettings[property] = true;
                if (typeof this.settings.properties[property].id !== 'undefined') {
                    this.optIn(property);
                }
                else if (typeof this.settings.properties[property].enableScriptTag !== 'undefined') {
                    this.enableScriptTag(this.settings.properties[property].enableScriptTag);
                }
            }
            else if (!checkbox.checked && this.consentSettings[property] === true) {
                this.consentSettings[property] = false;
                this.optOut(property, true);
            }
        }
        try {
            localStorage.setItem(this.localStorageIdentifier, JSON.stringify(this.consentSettings));
        }
        catch (error) {
            this.errors.push(error);
        }
        this.settings.debug && console.log('[ConsentManager]', 'setConsentSettings()', this.consentSettings);
        this.hideInterface();
        window.dispatchEvent(this.updateEvent);
    }

    /**
     * Get consent status for property
     *
     * @param {string} property
     * @returns {boolean|*}
     */
    getConsentStatusForProperty(property='')
    {
        if ('undefined' === typeof this.consentSettings[property]) {
            return false;
        }
        else return this.consentSettings[property];
    }

    /**
     * Get default consent settings
     *
     * Sets all consent properties to false
     *
     * @returns {object}
     */
    getDefaultConsentSettings()
    {
        let defaultConsentSettings = {};
        try {
            localStorage.removeItem(this.localStorageIdentifier);
            Object.keys(this.settings.properties).forEach((property) => {
                defaultConsentSettings[property] = false;
            });
        }
        catch (error) {
            this.settings.debug && console.error('[ConsentManager]', 'getDefaultConsentSettings()', error);
        }
        this.settings.debug && console.log('[ConsentManager]', 'getDefaultConsentSettings()', defaultConsentSettings);
        return defaultConsentSettings;
    }

    /**
     * Get interface
     *
     * @returns {void}
     */
    getInterface()
    {
        this.interface = document.getElementById('consent-manager');
        if (!this.interface) {
            this.buildInterface();
        }
    }

    /**
     * Get fingerprint icon which is a toggle button for the interface
     *
     * @returns {node|null}
     */
    getFingerPrintIcon()
    {
        if (!this.settings.fingerprintIcon) {
            return null;
        }
        let fingerPrintIcon = document.getElementById('consent-manager-toggle');
        if (!fingerPrintIcon) {
            fingerPrintIcon = document.createElement('a');
            fingerPrintIcon.id = 'consent-manager-toggle';
            fingerPrintIcon.classList.add('bottom-left'); // TODO: option to place it on the right, too?
            fingerPrintIcon.rel = 'consent-manager';
            fingerPrintIcon.innerHTML = '<?xml version="1.0" ?>\n' +
                '<svg height="48" viewBox="0 0 48 48" width="48" xmlns="http://www.w3.org/2000/svg">\n' +
                '    <path d="M35.62 8.94c-.16 0-.31-.04-.46-.11C31.33 6.85 28 6 24.02 6c-3.97 0-7.71.95-11.14 2.82-.49.26-1.09.09-1.36-.4-.26-.49-.09-1.09.4-1.36C15.65 5.03 19.72 4 24.02 4c4.26 0 7.98.94 12.06 3.05.49.25.68.86.43 1.35-.18.34-.53.54-.89.54zM7 19.44c-.2 0-.4-.06-.58-.18-.45-.32-.56-.94-.24-1.39 1.98-2.8 4.51-5 7.51-6.55 6.29-3.25 14.33-3.26 20.63-.02 2.99 1.54 5.51 3.72 7.5 6.5.32.45.22 1.07-.23 1.39-.45.32-1.08.22-1.4-.23-1.8-2.52-4.08-4.5-6.78-5.88-5.74-2.95-13.07-2.94-18.8.02-2.71 1.4-5 3.39-6.79 5.93-.2.27-.51.41-.82.41zm12.51 24.13c-.26 0-.51-.1-.71-.3-1.73-1.75-2.67-2.86-4.02-5.27-1.38-2.46-2.11-5.47-2.11-8.69 0-5.94 5.08-10.78 11.33-10.78s11.33 4.83 11.33 10.78c0 .55-.45 1-1 1s-1-.45-1-1c0-4.84-4.18-8.78-9.33-8.78-5.14 0-9.33 3.94-9.33 8.78 0 2.88.64 5.54 1.85 7.71 1.29 2.3 2.15 3.29 3.69 4.84.39.39.39 1.03-.01 1.41-.18.21-.44.3-.69.3zm14.33-3.7c-2.38 0-4.47-.6-6.2-1.77-2.97-2.02-4.75-5.3-4.75-8.78 0-.55.45-1 1-1s1 .45 1 1c0 2.81 1.45 5.47 3.88 7.12 1.41.96 3.07 1.43 5.07 1.43.48 0 1.29-.05 2.09-.19.54-.1 1.06.27 1.16.81.1.54-.27 1.06-.81 1.16-1.17.21-2.16.22-2.44.22zM29.81 44c-.09 0-.18-.01-.26-.04-3.19-.87-5.27-2.05-7.43-4.2-2.79-2.78-4.33-6.49-4.33-10.44 0-3.25 2.76-5.89 6.16-5.89 3.4 0 6.16 2.64 6.16 5.89 0 2.14 1.87 3.89 4.16 3.89s4.16-1.74 4.16-3.89c0-7.54-6.5-13.67-14.49-13.67-5.69 0-10.88 3.16-13.22 8.06-.78 1.62-1.17 3.51-1.17 5.61 0 1.56.14 4.02 1.33 7.21.19.52-.07 1.09-.59 1.29-.52.19-1.09-.07-1.29-.59-.98-2.63-1.46-5.21-1.46-7.91 0-2.4.46-4.58 1.37-6.47 2.67-5.58 8.57-9.19 15.02-9.19 9.09 0 16.49 7.03 16.49 15.67 0 3.25-2.77 5.89-6.16 5.89s-6.16-2.64-6.16-5.89c0-2.14-1.87-3.89-4.16-3.89s-4.16 1.74-4.16 3.89c0 3.41 1.33 6.62 3.74 9.02 1.89 1.88 3.73 2.92 6.55 3.69.53.15.85.7.7 1.23-.12.44-.52.73-.96.73z"/>\n' +
                '    <path d="M0 0h48v48H0z" fill="none"/>\n' +
                '</svg>';
            document.body.appendChild(fingerPrintIcon);
        }
        return fingerPrintIcon;
    }

    /**
     * Show interface
     *
     * @param {boolean} expandAllOptions
     * @returns {void}
     */
    showInterface(expandAllOptions=false)
    {
        if (this.showAllLink && expandAllOptions) {
            this.showAllLink.dispatchEvent(new Event('click'));
        }
        this.interface.classList.add('visible');
    }

    /**
     * Hide interface
     *
     * @returns {void}
     */
    hideInterface()
    {
        this.interface.classList.remove('visible');
    }

    /**
     * Build interface
     *
     * @returns {void}
     */
    buildInterface()
    {
        const locale = this.settings.localisation;
        this.interface = document.createElement('aside');
        this.interface.id = 'consent-manager';
        this.interface.classList.add(this.settings.interfaceLayout);
        this.settings.interfaceTheme && this.interface.classList.add(this.settings.interfaceTheme);
        let form = document.createElement('form');
        form.innerHTML = this.settings.interfaceHtml;
        for (const key in locale) {
            const value = locale[key];
            form.innerHTML = form.innerHTML.replace(`{${key}}`, value);
        }
        form.id = 'consent-manager__form';
        this.fieldset = document.createElement('fieldset');
        if (this.settings.interfaceMode == 'accept-all') {
            this.fieldset.style.display = 'none';
        }
        let reqLabel = document.createElement('label');
        let reqCheckbox = document.createElement('input');
        reqCheckbox.setAttribute('type', 'checkbox');
        reqCheckbox.setAttribute('checked', 1);
        reqCheckbox.setAttribute('readonly', true);
        reqCheckbox.setAttribute('onclick', 'return false;');
        reqLabel.appendChild(reqCheckbox);
        reqLabel.innerHTML += ` ${locale.requiredCookiesLabel}`;
        this.fieldset.appendChild(reqLabel);
        Object.keys(this.settings.properties).forEach((property) => {
            let label = document.createElement('label');
            label.setAttribute('for', `consent-property-${property}`);
            let checkbox = document.createElement('input');
            checkbox.setAttribute('type', 'checkbox');
            checkbox.id = `consent-property-${property}`;
            checkbox.classList.add('consent-property-checkbox');
            checkbox.name = `allow[${property}]`;
            if (this.consentSettings[property] === true) {
                checkbox.setAttribute('checked', true);
            }
            label.appendChild(checkbox);
            label.innerHTML += ` ${this.settings.properties[property].label || this.translationService.get('propertyLabel' + this.toCamelCase(property))}`;
            this.fieldset.appendChild(label);
        });
        form.appendChild(this.fieldset);
        if (this.settings.interfaceMode == 'accept-all') {
            this.acceptAllButton = document.createElement('button');
            this.acceptAllButton.textContent = locale.acceptAllLabel || this.translationService.get('acceptAllLabel');
            this.acceptAllButton.id = 'consent-manager__accept-all';
            form.appendChild(this.acceptAllButton);
        }
        this.acceptSelectedButton = document.createElement('button');
        this.acceptSelectedButton.textContent = locale.acceptSelectedLabel || this.translationService.get('acceptSelectedLabel');
        this.acceptSelectedButton.id = 'consent-manager__accept-selected';
        form.appendChild(this.acceptSelectedButton);
        if (this.settings.interfaceMode === 'accept-all') {
            this.acceptSelectedButton.style.display = 'none';
            this.showAllLink = document.createElement('a');
            this.showAllLink.textContent = locale.showOptionsLabel || this.translationService.get('showOptionsLabel');
            this.showAllLink.id = 'consent-manager__show-options';
            form.appendChild(this.showAllLink);
        }
        this.interface.appendChild(form);
        document.body.appendChild(this.interface);
        if (this.settings.toggleBoxes) {
            for (const checkbox of this.interface.querySelectorAll('input[type="checkbox"]')) {
                new ConsentManagerToggleBox(checkbox);
            }
        }
    }

    /**
     * String to camel case
     *
     * @param {string} string
     * @returns {string}
     */
    toCamelCase(string)
    {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    /**
     * Enable script tag by identifier
     *
     * @param identifier
     * @returns {void}
     */
    enableScriptTag(identifier='')
    {
        const scriptTag = document.querySelector(`script[type="text/plain"][data-consent="${identifier}"]`);
        if (scriptTag) {
            scriptTag.parentNode.removeChild(scriptTag);
            scriptTag.setAttribute('type', 'text/javascript');
            document.body.appendChild(scriptTag);
            this.settings.debug && console.log('[ConsentManager]', `enableScriptTag(${identifier})`, 'Enabled \<script\> with attribute data-consent="' + identifier + '"');
        }
        else {
            this.settings.debug && console.warn('[ConsentManager]', `enableScriptTag(${identifier})`, 'There is no \<script\> with attribute data-consent="' + identifier + '"');
        }
    }

    /**
     * Opt-in
     *
     * @param {string} property
     * @returns {void}
     */
    optIn(property)
    {
        this.settings.debug && console.log('[ConsentManager]', `optIn(${property})`);
        const id = this.settings.properties[property].id;
        switch (property) {
            case 'tagmanager':
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer', id);
                let noscript = document.createElement('noscript');
                let noscript__iframe = document.createElement('iframe');
                noscript__iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
                noscript__iframe.style.height = 0;
                noscript__iframe.style.width = 0;
                noscript__iframe.style.display = 'none';
                noscript__iframe.style.visibility = 'none';
                noscript.appendChild(noscript__iframe);
                document.body.insertBefore(noscript, document.body.firstChild);
                break;
            case 'analytics':
                let script = document.createElement('script');
                script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
                script.setAttribute('async', true);
                document.head.insertBefore(script, document.head.firstChild);
                if (typeof window[this.getDisableString(property)] !== 'undefined') {
                    window[this.getDisableString(property)] = false;
                }
                window.dataLayer = window.dataLayer ? window.dataLayer : [];
            function gtag() { dataLayer.push(arguments); }
                gtag('js', new Date());
                gtag('config', id, { 'anonymize_ip': true });
                break;
            case 'facebook':
                //Cookies.remove(fp_disableStr);
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                    n.queue=[];t=b.createElement(e);t.async=!0;
                    t.src=v;s=b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t,s)}(window,document,'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', id);
                fbq('track', 'PageView');
                break;
            case 'linkedin':
                window._linkedin_partner_id = id;
                window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ? window._linkedin_data_partner_ids : [];
                window._linkedin_data_partner_ids.push(window._linkedin_partner_id);
                (function() {
                    var s = document.getElementsByTagName('script')[0];
                    var b = document.createElement('script');
                    b.type = 'text/javascript';
                    b.async = true;
                    b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
                    s.parentNode.insertBefore(b, s);
                })();
                break;
        }
    }

    /**
     * Opt-out
     *
     * @param {string} property
     * @returns {void}
     */
    optOut(property, queuePageReload=false)
    {
        this.settings.debug && console.log('[ConsentManager]', `optOut(${property})`);
        switch (property) {
            case 'tagmanager':
                console.warn('Tagmanager has no optOut function');
                break;
            case 'analytics':
                window[this.getDisableString(property)] = true;
                break;
            case 'facebook':
                window[this.getDisableString(property)] = true;
                break;
            case 'linkedin':
                console.warn('LinkedIn has no optOut function');
                break;
        }
        if (queuePageReload) {
            this.queuePageReload();
        }
    }

    /**
     * Get disable string for a property
     *
     * @param {string} property
     * @returns {string}
     */
    getDisableString(property)
    {
        switch (property) {
            case 'analytics':
                return 'ga-disable-' + this.settings.properties[property].id;
            case 'facebook':
                return 'fb-pixel-is-disabled';
        }
    }

    /**
     * Reload the page
     *
     * @returns {void}
     */
    queuePageReload()
    {
        if (null === this.queuePageReloadTimeout) {
            let c = confirm(this.settings.localisation.queuePageReloadNotice);
            if (c) {
                this.queuePageReloadTimeout = setTimeout(() => {
                    window.location.reload();
                }, this.settings.localisation.queuePageReloadTimout);
            }
        }
    }

    /**
     * Check if the current consent settings configuration differs from a previous state
     *
     * @param {object} consentSettings
     * @returns {boolean}
     */
    hasSameSettingsAsBefore(consentSettings={})
    {
        const storedproperties = Object.keys(consentSettings);
        const actualproperties = Object.keys(this.settings.properties);
        if (storedproperties.length !== actualproperties.length) {
            return false;
        }
        let diff = 0;
        actualproperties.forEach((tracker, key) => {
            if (tracker !== storedproperties[key]) {
                diff ++;
            }}
        );
        return diff > 0 ? false : true;
    }

    /**
     * Set settings
     * @param {object} customSettings
     */
    setSettings(customSettings)
    {
        const defaultLocalisation = {
            interfaceHeader: this.translationService.get('interfaceHeader'),
            interfaceText: this.translationService.get('interfaceText'),
            requiredCookiesLabel: this.translationService.get('requiredCookiesLabel'),
            acceptSelectedLabel: this.translationService.get('acceptSelectedLabel'),
            acceptAllLabel: this.translationService.get('acceptAllLabel'),
            showOptionsLabel: this.translationService.get('showOptionsLabel'),
            queuePageReloadNotice: this.translationService.get('queuePageReloadNotice'),
        };
        const localisation = {...defaultLocalisation, ...customSettings.localisation};
        delete customSettings.localisation;
        const defaultSettings = {
            // Debug {boolean}
            debug: false,
            // If true, will replace all src="" with data-src="" for all embedded sources if no consent is given
            // Attention: while this is actually quite nice, it's still not 100 % legal, src should be escaped BEFORE page load
            disableFrames: true,
            // If true, will clear all youtube and google googlemaps cookies if no consent is given
            clearCookies: true,
            // Properties managed by this ConsentManager instance
            properties: {
                google_googlemaps: {
                    enableFrames: 'googlemaps' // Will enable all iframes with data-consent="googlemaps"
                },
                youtube: {
                    enableFrames: 'youtube' // Will enable all iframes with data-consent="youtube"
                },
                //analytics: {
                //    label: 'Override default label',
                //    id: 'UA-123456789'
                //},
                //customjs: {
                //    label: 'My custom JS tracker',
                //    enableScriptTag: 'customjs' // Will enable the script tag with type="text/plain" and data-consent="customjs"
                //}
            },
            // Interface theme {dark|light|null}
            interfaceTheme: ConsentManager.THEME_LIGHT,
            // Interface layout {bottom-left|bottom-wide|bottom-right|top-left|top-wide|top-right}
            interfaceLayout: ConsentManager.LAYOUT_BOTTOM_LEFT,
            // Interface mode {acceppt-all|accept-selected}
            interfaceMode: ConsentManager.INTERFACE_MODE_ACCEPT_ALL,
            // HTML template for the interface {string}
            interfaceHtml: '<h6>{interfaceHeader}</h6><p>{interfaceText}</p>',
            // Localisation, properties can be set manually or will be fetched from localisation serivce by default {object}
            localisation: localisation,
            // Wait for this many ms until a page is reloaded {int}
            queuePageReloadTimeout: 1000,
            // Auto-identify frames to block by these patterns {object}
            patterns: {
                youtube: 'youtu',
                googlemaps: 'googlemaps'
            },
            // Use toggle boxes instead of checkboxes {boolean}
            toggleBoxes: true,
            // Display a global fingerprint icon to show the consent manager {boolean}
            fingerprintIcon: true,
            // Identifier for the LocalStorage, will be auto-generated if null {string|null}
            localStorageIdentifier: null,
            // Mandatory: uri of the site's privacy notice {string}
            privacyUri: ''
        };
        this.settings = {...defaultSettings, ...customSettings};
        this.settings.debug && console.log('[ConsentManager]', 'setSettings()', this.settings);
    }

}

Object.defineProperty(ConsentManager, 'THEME_LIGHT', {
    value: 'theme-light',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'THEME_DARK', {
    value: 'theme-dark',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'LAYOUT_CENTER', {
    value: 'center',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'LAYOUT_BOTTOM_LEFT', {
    value: 'bottom-left',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'LAYOUT_BOTTOM_WIDE', {
    value: 'bottom-wide',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'LAYOUT_BOTTOM_RIGHT', {
    value: 'bottom-right',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'LAYOUT_TOP_LEFT', {
    value: 'top-left',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'LAYOUT_TOP_WIDE', {
    value: 'top-wide',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'LAYOUT_TOP_RIGHT', {
    value: 'top-right',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'INTERFACE_MODE_ACCEPT_ALL', {
    value: 'accept-all',
    writable : false,
    enumerable : true,
    configurable : false
});

Object.defineProperty(ConsentManager, 'INTERFACE_MODE_ACCEPT_SELECTED', {
    value: 'accept-selected',
    writable : false,
    enumerable : true,
    configurable : false
});

export default ConsentManager;